import type { Content } from '../types/content'

export const en: Content = {
  gate: {
    title: 'Choose Your Language',
    subtitle: 'Please select your preferred language to continue.',
    english: 'English',
    englishNote: 'Continue in English',
    spanish: 'Español',
    spanishNote: 'Continuar en español',
    footnote: 'You can change your language at any time from the header.',
  },
  switcher: {
    label: 'Change language',
    en: 'EN',
    es: 'ES',
  },
  nav: {
    links: [
      { label: 'Services', href: '#services' },
      { label: 'Why Us', href: '#why-us' },
      { label: 'Process', href: '#process' },
      { label: 'Testimonials', href: '#testimonials' },
      { label: 'FAQ', href: '#faq' },
    ],
    phone: '+1 (305) 555-0142',
    cta: 'Schedule Consultation',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    mobileCta: 'Schedule My Free Consultation',
    ariaLabel: 'Main',
    homeLabel: 'Standard Consulting Advice — home',
  },
  hero: {
    badge: 'Trusted by Entrepreneurs and Investors',
    headline: 'Expand Your Business into the United States',
    headlineAccent: 'Without Complications',
    subtitle:
      'We help entrepreneurs and investors establish, structure and grow companies in the United States through legal, financial and tax consulting.',
    primaryCta: 'Schedule My Free Consultation',
    secondaryCta: 'Learn More',
    imageAlt: 'Executive advisory team meeting inside a modern high-rise office',
    statLabel: 'Average timeline',
    statValue: '24–72 hrs',
    statBody: 'From signed engagement to a registered U.S. entity.',
    trustIndicators: [
      '100% Remote Process',
      'U.S. Specialists',
      'Fast Response',
      'Personalized Consulting',
      'Offices in Florida & New York',
    ],
  },
  socialProof: {
    eyebrow: 'Advising founders and investors since 2005',
    sectionLabel: 'Company results',
    metrics: [
      { value: '500+', label: 'Companies Assisted' },
      { value: '20+', label: 'Years of Experience' },
      { value: '100%', label: 'Remote Process' },
      { value: 'USA + LATAM', label: 'International Clients' },
    ],
  },
  pain: {
    heading: "Running a Business Shouldn't Feel Complicated",
    cards: [
      {
        title: "Don't know how to create an LLC?",
        body: 'State selection, operating agreements, registered agents — one wrong step delays everything for months.',
      },
      {
        title: 'Unsure about taxes?',
        body: 'Federal, state and international filings each carry deadlines, and the IRS does not send reminders.',
      },
      {
        title: 'Need accounting and compliance?',
        body: 'Bookkeeping, payroll and annual reports pile up fast and quietly put your company at risk.',
      },
    ],
    bottom: 'We take care of everything while you focus on growing your business.',
    cta: 'Talk to an Expert',
  },
  services: {
    eyebrow: 'Services',
    heading: 'Everything Your Business Needs in One Place',
    subtitle:
      'Four practice areas, one accountable team — so nothing falls between your lawyer, your accountant and your bank.',
    groups: [
      {
        title: 'Business Formation',
        description: 'Everything required to stand up a compliant U.S. entity from day one.',
        icon: 'building',
        items: ['LLC', 'Corporation', 'EIN', 'Business Bank Account', 'Registered Agent'],
      },
      {
        title: 'Asset Protection',
        description: 'Structures that separate your wealth from your operating risk.',
        icon: 'shield',
        items: ['Holdings', 'Trusts', 'Legal Structures', 'Estate Planning', 'Tax Optimization'],
      },
      {
        title: 'Accounting & Payroll',
        description: 'A full back office managed by U.S. specialists, month after month.',
        icon: 'calculator',
        items: ['Bookkeeping', 'Payroll', 'Financial Reports', 'Compliance', 'Outsourcing'],
      },
      {
        title: 'Tax Services',
        description: 'Planning and filing handled with Big Four discipline.',
        icon: 'receipt',
        items: ['Personal Taxes', 'Corporate Taxes', 'IRS Compliance', 'ITIN', 'Tax Planning'],
      },
    ],
  },
  whyUs: {
    eyebrow: 'Why us',
    heading: 'Why Businesses Choose Standard Consulting Advice',
    items: [
      {
        title: 'International Experience',
        body: 'Two decades advising founders and investors across the United States and Latin America.',
      },
      {
        title: 'Personalized Attention',
        body: 'No ticket queues. You speak directly with the advisor who knows your structure.',
      },
      {
        title: 'Big Four Standards',
        body: 'Methodology, documentation and controls modeled on top-tier global firms.',
      },
      {
        title: 'Fast Processes',
        body: 'Formation, EIN and banking moved forward in parallel, not one step at a time.',
      },
      {
        title: 'Dedicated Advisors',
        body: 'A named point of contact for legal, financial and tax questions year-round.',
      },
      {
        title: 'Long-Term Partnership',
        body: 'We stay after incorporation — through growth, hiring and every filing season.',
      },
    ],
  },
  process: {
    eyebrow: 'Process',
    heading: 'A Clear Path From First Call to Full Support',
    steps: [
      {
        step: 'STEP 1',
        title: 'Book a Free Consultation',
        body: 'A 30-minute call to understand your goals, timeline and current structure.',
      },
      {
        step: 'STEP 2',
        title: 'Business Assessment',
        body: 'We review your operation, residency, revenue model and tax exposure.',
      },
      {
        step: 'STEP 3',
        title: 'Strategic Plan',
        body: 'You receive a written recommendation: entity, state, structure and costs.',
      },
      {
        step: 'STEP 4',
        title: 'Implementation',
        body: 'Registration, EIN, banking and compliance filings executed by our team.',
      },
      {
        step: 'STEP 5',
        title: 'Continuous Support',
        body: 'Ongoing accounting, payroll and tax planning with a dedicated advisor.',
      },
    ],
  },
  formation: {
    eyebrow: 'Remote formation',
    heading: 'Open Your U.S. Company Completely Online',
    body: 'Our team manages every step of the process, from business registration to obtaining your EIN and opening business bank accounts, allowing entrepreneurs to establish their companies remotely with confidence and efficiency.',
    cta: 'Start Today',
    imageAlt: 'Two professionals signing company incorporation documents',
    badgeLabel: 'Signed remotely',
    badgeBody: 'Zero paperwork mailed. Zero flights booked.',
  },
  asset: {
    eyebrow: 'Wealth & structure',
    heading: "Protect What You've Built, Then Structure It to Grow",
    body: 'High-net-worth founders and investors need more than an entity. We design the legal and tax architecture that keeps wealth separated, efficient and transferable.',
    pillars: [
      {
        title: 'Asset Protection',
        body: 'Insulate personal and family wealth from business liability with layered entities.',
      },
      {
        title: 'Tax Optimization',
        body: 'Legally reduce your effective rate through the right entity and treaty planning.',
      },
      {
        title: 'Holdings',
        body: 'Consolidate subsidiaries under a single holding for clean control and exits.',
      },
      {
        title: 'Trust Structures',
        body: 'Domestic and international trusts built for privacy and succession.',
      },
      {
        title: 'Long-Term Wealth Strategy',
        body: 'A multi-generational plan reviewed annually as your portfolio grows.',
      },
    ],
    vaultAlt: 'Modern steel vault door in a dark luxury interior',
    entrepreneurAlt: 'Entrepreneur standing by a skyscraper window overlooking the city',
    officeAlt: 'Interior of a luxury executive office suite',
  },
  outsourcing: {
    eyebrow: 'Outsourcing',
    heading: 'Focus on Growing While We Handle Your Finances',
    subtitle:
      'A complete outsourced finance department — reconciled books, paid teams and reports you can actually make decisions with.',
    items: [
      { title: 'Accounting', body: 'Monthly bookkeeping reconciled and closed on schedule.' },
      { title: 'Payroll', body: 'Employees and contractors paid, filed and documented.' },
      { title: 'Compliance', body: 'Annual reports, registered agent and state obligations tracked.' },
      { title: 'Financial Reports', body: 'P&L, balance sheet and cash flow you can act on.' },
      { title: 'Monthly Support', body: 'A standing call with your advisor to review the numbers.' },
    ],
    imageAlt: 'Illustration of a financial dashboard with cash flow, payroll and compliance widgets',
  },
  tax: {
    eyebrow: 'Tax services',
    heading: 'Tax Planning That Saves You Time and Money',
    body: 'Filing is the deadline, not the strategy. We map your obligations across federal, state and cross-border rules, then plan the year so your effective rate is a decision — not a surprise.',
    items: [
      'IRS Compliance',
      'Personal Taxes',
      'Corporate Taxes',
      'Amendments',
      'ITIN',
      'Strategic Tax Planning',
    ],
    cta: 'Review My Tax Situation',
    imageAlt: 'Tax advisor reviewing financial documents with a client',
    badgeLabel: 'Client average',
    badgeValue: '$18,400',
    badgeBody: 'Annual tax savings identified in the first planning cycle.',
  },
  testimonials: {
    eyebrow: 'Testimonials',
    heading: 'Trusted by Founders and Investors Across the Americas',
    ratingLabel: 'Rated 5 out of 5',
    items: [
      {
        quote: 'Opening our U.S. company was easier than expected.',
        name: 'Mariana Duarte',
        role: 'Founder, Logistics SaaS',
        location: 'São Paulo, BR',
      },
      {
        quote: 'Excellent communication and professional guidance.',
        name: 'Andrés Villalba',
        role: 'Managing Partner',
        location: 'Bogotá, CO',
      },
      {
        quote: 'Their tax planning saved us thousands.',
        name: 'Rachel Kim',
        role: 'CFO, E-commerce Group',
        location: 'Miami, FL',
      },
      {
        quote: 'Fast, transparent and reliable.',
        name: 'Diego Fuentes',
        role: 'Real Estate Investor',
        location: 'Mexico City, MX',
      },
      {
        quote: 'They became our long-term strategic partner.',
        name: 'Sofía Ramírez',
        role: 'COO, Fintech',
        location: 'Buenos Aires, AR',
      },
      {
        quote: 'Highly recommended.',
        name: 'Thomas Whitfield',
        role: 'Angel Investor',
        location: 'New York, NY',
      },
    ],
  },
  faq: {
    eyebrow: 'FAQ',
    heading: 'Questions Founders Ask Us First',
    body: 'Still unsure? Bring it to the free consultation — there is no obligation and no sales script.',
    items: [
      {
        question: 'How long does incorporation take?',
        answer:
          'Most entities are registered within 24 to 72 hours depending on the state. Your EIN typically follows within 1 to 3 weeks, and business banking can be opened as soon as it is issued.',
      },
      {
        question: 'Can everything be done remotely?',
        answer:
          'Yes. Formation, EIN, banking, ITIN and every ongoing filing are handled 100% online. Documents are signed electronically and you receive a secure digital file with everything.',
      },
      {
        question: 'Do I need to travel to the U.S.?',
        answer:
          'No travel is required. Our process is built for non-resident founders and investors, including banking partners that onboard clients without a U.S. visit.',
      },
      {
        question: 'Can you help with taxes every year?',
        answer:
          'We file federal, state and information returns annually and plan ahead each quarter so there are no surprises in April.',
      },
      {
        question: 'Do you offer accounting after incorporation?',
        answer:
          'Yes. Bookkeeping, payroll, compliance calendars and monthly financial reporting are available as an ongoing engagement with a dedicated advisor.',
      },
    ],
  },
  finalCta: {
    heading: 'Ready to Grow Your Business in the United States?',
    subtitle: 'Book a free consultation and receive a personalized strategy for your business.',
    assurances: ['Free 30-minute call', 'No obligation', 'Answer within 24 hours'],
  },
  form: {
    heading: 'Schedule Consultation',
    name: 'Full name',
    namePlaceholder: 'Jane Doe',
    email: 'Email',
    emailPlaceholder: 'you@company.com',
    phone: 'Phone (optional)',
    phonePlaceholder: '+1 (305) 555-0142',
    goal: 'What do you need help with?',
    goalPlaceholder: 'Select an option',
    goalOptions: [
      'Open a U.S. company',
      'Asset protection & holdings',
      'Accounting & payroll',
      'Tax planning & filings',
      'Something else',
    ],
    submit: 'Schedule Consultation',
    submitting: 'Sending…',
    privacy: 'We reply within 24 hours. Your information is never shared.',
    errors: {
      name: 'Please enter your full name.',
      email: 'Please enter your email address.',
      emailInvalid: 'Please enter a valid email address.',
      goal: 'Please select what you need help with.',
      generic: 'Please correct the highlighted fields to continue.',
    },
    success: {
      title: 'Your consultation request was received.',
      body: 'An advisor will contact you within 24 hours with available times and next steps.',
      again: 'Send another request',
    },
  },
  footer: {
    tagline:
      'Legal, financial and tax consulting for entrepreneurs and investors building companies in the United States.',
    floridaOffice: 'Florida Office',
    floridaAddress: ['261 N University Dr Ste 500', 'Plantation, Florida 33324'],
    newYorkOffice: 'New York Office',
    newYorkAddress: ['418 Broadway Ste Y', 'Albany, New York 12207'],
    contact: 'Contact',
    explore: 'Explore',
    teamPhotoAlt: 'Standard Consulting Advice partners at an industry event in Las Vegas',
    freeConsultation: 'Free Consultation',
    rights: 'Standard Consulting Advice. All rights reserved.',
    offices: 'Offices in Florida & New York · Serving USA + LATAM',
  },
}
